import React, { useState } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';

import { Text, Title, Box } from '@nerdwallet/currency';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { connect } from 'react-redux';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import {
  stringTextAreaSpec,
  boolCheckboxSpec,
  stringInputSpec,
  stringSelectSpec,
  preheaderInput,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';

import BackgroundColor from '../background-color/background-color';
import SmartButton from '../smart-button/smart-button';

import './border-banner.less';
import BorderBannerBackgroundWrapper from './border-banner-background-wrapper';

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const BorderBanner = ({
  preheader,
  title,
  subtitle,
  titleSize,
  bgOptions,
  borderBgOptions,
  fontChronicle,
  roundedCorners,
  backgroundSize,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  size,
  desktopAlignment,
  mobileAlignment,
  backgroundImage,
  dangerouslyAllowHTMLInSubtitle,
  dangerouslyAllowHTMLInTitle,
  // For SmartButton
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  ...smartButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: buttonURL,
        params: {
          [TrkTag.location]: 'BorderBanner',
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `border-banner-${trkId}`,
    elementName: 'BorderBanner',
  };

  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };

  const { textColor: titleTextColor } = getTextColor(titleColorOptions);
  const titleColorStyle = { color: titleTextColor };

  const shouldShowActionButton =
    smartButtonProps?.buttonType && smartButtonProps?.buttonType !== 'none';

  return (
    <Impression eventType="element_impression" entityName="border_banner">
      <ElementImpression {...elementImpressionProps}>
        <Box className={classes('text-align-center')}>
          <BorderBannerBackgroundWrapper
            bgOptions={borderBgOptions}
            size={size}
            className={classNames('bg-border', {
              'rounded-corners': roundedCorners,
            })}
          >
            <BorderBannerBackgroundWrapper
              bgOptions={bgOptions}
              size={size}
              className={classNames('border-banner', {
                'rounded-corners': roundedCorners,
              })}
            >
              <Box
                className={classNames([
                  'border-banner__content',
                  'centered-in-parent',
                  { [classes('padding-bottom-4')]: !shouldShowActionButton },
                ])}
              >
                <Box
                  style={titleColorStyle}
                  className="border-banner__preheader"
                >
                  <Text size="small">{linkParser(preheader)}</Text>
                </Box>
                <Title
                  size={titleSize}
                  className={classNames([
                    'border-banner__title',
                    {
                      gotham: !fontChronicle,
                    },
                  ])}
                  style={titleColorStyle}
                >
                  {htmlOutputParser(title, dangerouslyAllowHTMLInTitle)}
                </Title>
                <Box style={colorStyle}>
                  <Text>
                    {htmlOutputParser(subtitle, dangerouslyAllowHTMLInSubtitle)}
                  </Text>
                </Box>
                {shouldShowActionButton && (
                  <Box
                    className={classNames([
                      classes('padding-top-4'),
                      classes('padding-bottom-4'),
                    ])}
                  >
                    <SmartButton
                      buttonURL={modifiedUrl}
                      buttonText={buttonText}
                      buttonClass="border-banner__button"
                      {...smartButtonProps}
                    />
                  </Box>
                )}
              </Box>
            </BorderBannerBackgroundWrapper>
          </BorderBannerBackgroundWrapper>
        </Box>
      </ElementImpression>
    </Impression>
  );
};

BorderBanner.displayName = 'BorderBanner';

BorderBanner.propTypes = {
  title: PropTypes.string,
  titleSize: PropTypes.oneOf(['small', 'default']),
  subtitle: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  fontChronicle: PropTypes.bool,
  roundedCorners: PropTypes.bool,
  ...SmartButton.propTypes,

  backgroundImage: PropTypes.bool,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  desktopAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  mobileAlignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  dangerouslyAllowHTMLInSubtitle: PropTypes.bool,
  dangerouslyAllowHTMLInTitle: PropTypes.bool,
};

BorderBanner.defaultProps = {
  buttonType: 'custom',
  fontChronicle: false,
  roundedCorners: false,
  size: '',
  titleSize: 'small',
  bgOptions: {
    hexColor: '',
    backgroundColor: 'green-dark',
    backgroundSize: 'cover',
  },
  borderBgOptions: {
    hexColor: '',
    backgroundColor: 'green-light',
    backgroundSize: 'cover',
  },
  ...SmartButton.defaultProps,
  dangerouslyAllowHTMLInSubtitle: false,
  dangerouslyAllowHTMLInTitle: false,
};

const borderBannerImageTypeInput = (baseName, fieldName, label, helpText) => [
  boolCheckboxSpec(`${baseName}.${fieldName}`, label, {
    help: helpText,
  }),
  stringSelectSpec(
    `${baseName}.backgroundSize`,
    'Background Size',
    [
      { label: 'Cover', value: 'cover' },
      { label: 'Contain', value: 'contain' },
      { label: 'Auto', value: 'auto' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    `${baseName}.desktopAlignment`,
    'Desktop Alignment',
    [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    `${baseName}.mobileAlignment`,
    'Mobile Alignment',
    [
      { label: 'Top', value: 'top' },
      { label: 'Middle', value: 'center' },
      { label: 'Bottom', value: 'bottom' },
    ],
    {
      help: 'Only applies to Mobile',
    }
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcDesktop`,
    'Background Image URL (Desktop)'
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcTablet`,
    'Background Image URL (Tablet)'
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcMobile`,
    'Background Image URL (Mobile)'
  ),
];

BorderBanner.getEditingConfig = (componentDocument) => {
  const borderBannerForeGroundImageTypeInputConfig =
    get(componentDocument, 'props.bgOptions.backgroundImage', false) === true
      ? [
          ...borderBannerImageTypeInput(
            'bgOptions',
            'backgroundImage',
            'Enable foreground Image?',
            'If checked enables foreground image output.'
          ),
        ]
      : [
          borderBannerImageTypeInput(
            'bgOptions',
            'backgroundImage',
            'Enable foreground Image?',
            'If checked enables foreground image output.'
          )[0],
        ];

  const borderBannerBackgroundImageTypeInputConfig =
    get(componentDocument, 'props.borderBgOptions.backgroundImage', false) ===
    true
      ? [
          ...borderBannerImageTypeInput(
            'borderBgOptions',
            'backgroundImage',
            'Enable background Image?',
            'If checked enables background image output.'
          ),
        ]
      : [
          borderBannerImageTypeInput(
            'borderBgOptions',
            'backgroundImage',
            'Enable background Image?',
            'If checked enables background image output.'
          )[0],
        ];

  return [
    ...BackgroundColor.getEditingConfig(
      componentDocument,
      'borderBgOptions',
      'Background Color'
    ),
    ...BackgroundColor.getEditingConfig(
      componentDocument,
      'bgOptions',
      'Foreground Color'
    ),
    ...borderBannerBackgroundImageTypeInputConfig,
    ...borderBannerForeGroundImageTypeInputConfig,
    preheaderInput,
    boolCheckboxSpec('dangerouslyAllowHTMLInTitle', 'Allow HTML in Title?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    stringTextAreaSpec('title', 'Title', {
      help: `Want a hyperlink? Write it like this: [Hi!](www.thing.com).
           NW links should be relative, e.g., [Home](/home)`,
    }),
    stringSelectSpec(
      `titleSize`,
      'Title Size',
      [
        { label: 'Small', value: 'small' },
        { label: 'Default', value: 'default' },
      ],
      {
        help: 'Defaults to small',
      }
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
    boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
    boolCheckboxSpec('roundedCorners', 'Rounded Corners?'),
    boolCheckboxSpec(
      'dangerouslyAllowHTMLInSubtitle',
      'Allow HTML in Subtitle?',
      {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }
    ),
    stringTextAreaSpec('subtitle', 'Subtitle?', {
      help: `Want a hyperlink? Write it like this: [Hi!](www.thing.com).
           NW links should be relative, e.g., [Home](/home)`,
    }),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'textColorOptions',
      'Subtitle Color'
    ),
    ...SmartButton.getEditingConfig(componentDocument),
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(BorderBanner);
